/**
 * Shared "change an IO's status" logic, used by both the Dashboard's inline
 * status dropdown and the Review pane's action bar.
 *
 * Approve/reject have dedicated backend endpoints (they record extra fields
 * — approver, rejection reason) so those still go through approveIo/rejectIo.
 * Everything else routes through the generic PATCH /:id/status endpoint.
 *
 * Uploaded IOs are now saved to the backend DB on upload, so approve/reject
 * always calls the backend API. For backward compat, local-only IOs (source
 * "upload") that don't exist in the backend will fall back to localStorage.
 */
import { approveIo, rejectIo, setIoStatus } from "../api.js";
import { updateUploadedIoStatus } from "./localIos.js";
import { STATUS_MAP } from "../components/StatusBadge.jsx";

// Deduped, selectable subset of STATUS_MAP — "pending" shares "draft"'s label
// so it's omitted, and "deleted" is handled by the dedicated Remove action.
export const STATUS_OPTIONS = ["draft", "approved", "rejected"].map((key) => ({
  value: key,
  label: STATUS_MAP[key].label,
}));

/**
 * Change `io`'s status. Returns true if the change was applied, false if the
 * user cancelled (e.g. the reject-reason prompt). Throws ApiError on backend
 * failure — callers should catch and surface it.
 */
export async function changeIoStatus(io, newStatus) {
  // For approve/reject, always try the backend first (uploaded IOs are now
  // saved to the backend DB on upload). If the backend call fails with a 404
  // (IO not in backend), fall back to localStorage for local-only IOs.
  if (newStatus === "approved") {
    try {
      await approveIo(io.io_number || io.id, { approver: "longship@adgentek.ai" });
      if (io.source === "upload") updateUploadedIoStatus(io.id, newStatus);
      return true;
    } catch (err) {
      if (err.status === 404 && io.source === "upload") {
        updateUploadedIoStatus(io.id, newStatus);
        return true;
      }
      throw err;
    }
  }
  if (newStatus === "rejected") {
    const reason = window.prompt("Reason for rejection?");
    if (!reason || !reason.trim()) return false;
    try {
      await rejectIo(io.io_number || io.id, { reason: reason.trim() });
      if (io.source === "upload") updateUploadedIoStatus(io.id, newStatus);
      return true;
    } catch (err) {
      if (err.status === 404 && io.source === "upload") {
        updateUploadedIoStatus(io.id, newStatus);
        return true;
      }
      throw err;
    }
  }
  // Other status changes: try backend, fall back to localStorage
  if (io.source === "upload") {
    try {
      await setIoStatus(io.io_number || io.id, newStatus);
    } catch {
      // Not in backend — just update localStorage
    }
    updateUploadedIoStatus(io.id, newStatus);
    return true;
  }
  await setIoStatus(io.id, newStatus);
  return true;
}
